import React, { useState, useEffect } from 'react';
import { Menu, X, Instagram, Mail } from 'lucide-react';

const Header = ({
  onReserveClick,
  siteMediaEnabled = true,
  forceOpaque = false,
  showReserve = true,
  socialLinks = {},
  hideBrandUntilHeroOut = false,
  isHeroInView = false,
  enableBrandBridge = false,
  onBrandTextElementChange
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const isHeaderOpaque = isScrolled || forceOpaque;
  const isBrandAnchorVisible = forceOpaque || enableBrandBridge || !hideBrandUntilHeroOut || !isHeroInView;
  const isBrandTextVisible = forceOpaque || !hideBrandUntilHeroOut || !isHeroInView;

  useEffect(() => {
    let ticking = false;

    const syncScrolled = () => {
      const nextScrolled = window.scrollY > 50;
      setIsScrolled(prev => (prev === nextScrolled ? prev : nextScrolled));
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(syncScrolled);
    };

    syncScrolled();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const ids = ['concept', 'events', 'menu', 'media', 'lieu'];
    let ticking = false;
    const chooseActive = () => {
      const viewportCenter = window.innerHeight / 2;
      const measures = ids.map(id => {
        const el = document.getElementById(id);
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        return { id, rectTop: rect.top, rectBottom: rect.top + rect.height, distance: Math.abs(center - viewportCenter) };
      }).filter(Boolean);
      const candidates = measures.filter(m => m.rectBottom > 0 && m.rectTop < window.innerHeight);
      if (candidates.length === 0) {
        setActiveSection('');
        return;
      }
      const next = candidates.sort((a, b) => a.distance - b.distance)[0];
      if (next) setActiveSection(next.id);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          chooseActive();
          ticking = false;
        });
      }
    };
    const onResize = () => chooseActive();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    chooseActive();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const scrollToSection = (id) => {
    if (window.location.pathname !== '/') {
      window.location.href = `/#${id}`;
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
    } else {
      setActiveSection('');
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter,box-shadow] duration-300 ${
        isHeaderOpaque
          ? 'bg-black/92 md:bg-black/84 border-b border-white/12 shadow-[0_14px_38px_rgba(0,0,0,0.28)] backdrop-blur-xl'
          : 'bg-black/86 md:bg-black/72 border-b border-transparent md:border-white/10 shadow-[0_10px_26px_rgba(0,0,0,0.18)] backdrop-blur-lg'
      }`}
    >
      <div className="flex justify-between items-center px-6 py-3.5 mx-auto max-w-7xl min-h-[4.5rem] md:px-8 md:py-4">
        <div className="flex items-center min-w-0 md:min-w-[9.5rem]">
          <a
            href="/"
            className={`flex gap-3 items-center min-w-0 transform-gpu transition-[opacity,transform] duration-300 sm:gap-4 md:gap-5 ${
              isBrandAnchorVisible
                ? 'opacity-100 translate-y-0 pointer-events-auto'
                : 'opacity-0 -translate-y-1 pointer-events-none'
            }`}
          >
            <div
              ref={onBrandTextElementChange || undefined}
              className="text-2xl font-black tracking-tighter shrink-0 transition-opacity duration-200"
              style={{ opacity: isBrandTextVisible ? 1 : 0 }}
            >
              <span className="block leading-none text-center">CRA</span>
              <span className="block leading-none text-center">PULE</span>
            </div>
            {/* comment here to hide the venue signature  
            <VenueSignature
              className="gap-2.5 px-2.5 py-1.5 sm:px-3 sm:py-2"
              labelClassName="text-[8px] tracking-[0.22em] sm:text-[9px]"
              logoClassName="w-[5.25rem] h-[1.45rem] sm:w-[6.25rem] sm:h-[1.7rem] md:w-[7rem] md:h-[1.9rem]"
            />*/} 
          </a>
        </div>

        <nav role="navigation" aria-label="Section navigation" className="hidden gap-10 items-center md:flex">
          <button 
            onClick={() => scrollToSection('concept')}
            aria-current={activeSection === 'concept' ? 'true' : undefined}
            className={`relative text-sm uppercase tracking-widest transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white after:content-[''] after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-full after:bg-white/70 after:scale-x-0 after:transition-transform after:duration-200 hover:after:scale-x-100 ${activeSection === 'concept' ? 'text-white after:scale-x-100' : ''}`}
          >
            Concept
          </button>
          <button 
            onClick={() => scrollToSection('events')}
            aria-current={activeSection === 'events' ? 'true' : undefined}
            className={`relative text-sm uppercase tracking-widest transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white after:content-[''] after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-full after:bg-white/70 after:scale-x-0 after:transition-transform after:duration-200 hover:after:scale-x-100 ${activeSection === 'events' ? 'text-white after:scale-x-100' : ''}`}
          >
            Éditions
          </button>
          <button 
            onClick={() => scrollToSection('menu')}
            aria-current={activeSection === 'menu' ? 'true' : undefined}
            className={`relative text-sm uppercase tracking-widest transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white after:content-[''] after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-full after:bg-white/70 after:scale-x-0 after:transition-transform after:duration-200 hover:after:scale-x-100 ${activeSection === 'menu' ? 'text-white after:scale-x-100' : ''}`}
          >
            Menu
          </button>
          {siteMediaEnabled && (
            <button 
              onClick={() => scrollToSection('media')}
              aria-current={activeSection === 'media' ? 'true' : undefined}
              className={`relative text-sm uppercase tracking-widest transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white after:content-[''] after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-full after:bg-white/70 after:scale-x-0 after:transition-transform after:duration-200 hover:after:scale-x-100 ${activeSection === 'media' ? 'text-white after:scale-x-100' : ''}`}
            >
              Galerie
            </button>
          )}
          <button 
            onClick={() => scrollToSection('lieu')}
            aria-current={activeSection === 'lieu' ? 'true' : undefined}
            className={`relative text-sm uppercase tracking-widest transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white after:content-[''] after:absolute after:bottom-[-6px] after:left-0 after:h-[1px] after:w-full after:bg-white/70 after:scale-x-0 after:transition-transform after:duration-200 hover:after:scale-x-100 ${activeSection === 'lieu' ? 'text-white after:scale-x-100' : ''}`}
          >
            Lieu
          </button>
          {(socialLinks?.instagram || socialLinks?.email) && (
            <div className="flex gap-4 items-center pl-5 ml-1 border-l border-white/10">
              {socialLinks?.instagram && (
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="text-gray-300 transition-colors hover:text-white"
                >
                  <Instagram size={18} />
                </a>
              )}
              <a
                href={`mailto:${socialLinks?.email || 'contact@crapule.xyz'}`}
                aria-label="Email"
                className="text-gray-300 transition-colors hover:text-white"
              >
                <Mail size={18} />
              </a>
            </div>
          )}
          {showReserve && (
            <button 
              onClick={onReserveClick}
              className="px-6 py-2.5 text-sm font-semibold tracking-widest text-black uppercase bg-white rounded-full transition-colors duration-200 hover:bg-gray-200"
            >
              Réserver
            </button>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-white bg-black/86 border border-white/10 shadow-[0_10px_24px_rgba(0,0,0,0.24)] relative z-[60]"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Ouvrir le menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="fixed inset-x-0 bottom-0 top-[4.75rem] border-t md:hidden bg-neutral-950 border-white/12 shadow-[0_18px_40px_rgba(0,0,0,0.42)]">
          <nav role="navigation" aria-label="Section navigation" className="flex flex-col gap-4 px-6 py-6 min-h-full bg-neutral-950">
            <button 
              onClick={() => scrollToSection('concept')}
              aria-current={activeSection === 'concept' ? 'true' : undefined}
              className={`text-left text-sm uppercase tracking-widest py-2 transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white ${activeSection === 'concept' ? 'text-white' : ''}`}
            >
              Concept
            </button>
            <button 
              onClick={() => scrollToSection('events')}
              aria-current={activeSection === 'events' ? 'true' : undefined}
              className={`text-left text-sm uppercase tracking-widest py-2 transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white ${activeSection === 'events' ? 'text-white' : ''}`}
            >
              Éditions
            </button>
            <button 
              onClick={() => scrollToSection('menu')}
              aria-current={activeSection === 'menu' ? 'true' : undefined}
              className={`text-left text-sm uppercase tracking-widest py-2 transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white ${activeSection === 'menu' ? 'text-white' : ''}`}
            >
              Menu
            </button>
            {siteMediaEnabled && (
              <button 
                onClick={() => scrollToSection('media')}
                aria-current={activeSection === 'media' ? 'true' : undefined}
                className={`text-left text-sm uppercase tracking-widest py-2 transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white ${activeSection === 'media' ? 'text-white' : ''}`}
              >
                Galerie
              </button>
            )}
            <button 
              onClick={() => scrollToSection('lieu')}
              aria-current={activeSection === 'lieu' ? 'true' : undefined}
              className={`text-left text-sm uppercase tracking-widest py-2 transition-colors duration-200 cursor-pointer text-gray-300 hover:text-white ${activeSection === 'lieu' ? 'text-white' : ''}`}
            >
              Lieu
            </button>
            {showReserve && (
              <button 
                onClick={() => {
                  onReserveClick();
                  setIsMobileMenuOpen(false);
                }}
                className="px-6 py-3 mt-4 text-sm font-semibold tracking-widest text-black uppercase bg-white rounded-full transition-colors duration-200 hover:bg-gray-200"
              >
                Réserver
              </button>
            )}
            {(socialLinks?.instagram || socialLinks?.email) && (
              <div className="flex gap-4 items-center pt-4 mt-2 border-t border-white/10">
                {socialLinks?.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="inline-flex justify-center items-center w-10 h-10 text-gray-300 rounded-full hover:text-white"
                  >
                    <Instagram size={20} />
                  </a>
                )}
                <a
                  href={`mailto:${socialLinks?.email || 'contact@crapule.xyz'}`}
                  aria-label="Email"
                  className="inline-flex justify-center items-center w-10 h-10 text-gray-300 rounded-full hover:text-white"
                >
                  <Mail size={20} />
                </a>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
